import React, { useState } from "react";
import { useHR } from "../HRContext";
import { HRDepartment, HREmployee } from "../../types/hr";
import DepartmentForm from "./DepartmentForm";
import {
  Layout,
  Card,
  Row,
  Col,
  Button,
  Typography,
  Tag,
  List,
  Avatar,
  Empty,
  Modal,
  message,
  Spin,
  Statistic,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UserOutlined,
  DollarOutlined,
} from "@ant-design/icons";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const DepartmentList: React.FC = () => {
  const { departments, employees, loading, deleteDepartment } = useHR();
  const [showForm, setShowForm] = useState(false);
  const [editingDepartment, setEditingDepartment] =
    useState<HRDepartment | null>(null);

  const handleDelete = async (department: HRDepartment) => {
    const employeeCount = employees.filter(
      (emp: HREmployee) => emp.department_id === department.id
    ).length;

    if (employeeCount > 0) {
      Modal.warning({
        title: "Suppression impossible",
        content: `Impossible de supprimer ce département car ${employeeCount} employé(s) y sont rattaché(s).`,
      });
      return;
    }

    Modal.confirm({
      title: "Confirmation de suppression",
      content: `Êtes-vous sûr de vouloir supprimer le département "${department.name}" ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await deleteDepartment(department.id);
          message.success("Département supprimé avec succès");
        } catch (error) {
          message.error("Erreur lors de la suppression du département");
        }
      },
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-BF", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading.departments) {
    return (
      <Layout>
        <Content style={{ padding: "24px" }}>
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: "24px" }}>
        {/* Header */}
        <Card className="mb-6" bordered={false}>
          <div className="flex justify-between items-center">
            <div>
              <Title level={2} className="!mb-2">
                Gestion des Départements
              </Title>
              <Text type="secondary">{departments.length} département(s)</Text>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingDepartment(null);
                setShowForm(true);
              }}
            >
              Nouveau Département
            </Button>
          </div>
        </Card>

        {/* Grille des départements */}
        {departments.length > 0 ? (
          <Row gutter={[16, 16]}>
            {departments.map((department: HRDepartment) => {
              const departmentEmployees = employees.filter(
                (emp: HREmployee) => emp.department_id === department.id
              );
              const manager = employees.find(
                (emp: HREmployee) => emp.id === department.manager_id
              );

              return (
                <Col xs={24} sm={12} lg={8} xl={6} key={department.id}>
                  <Card
                    hoverable
                    actions={[
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingDepartment(department);
                          setShowForm(true);
                        }}
                        title="Modifier"
                      />,
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(department)}
                        title="Supprimer"
                      />,
                    ]}
                    className="h-full"
                  >
                    {/* Header */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <TeamOutlined className="text-blue-600" />
                        <Title level={4} className="!mb-0">
                          {department.name}
                        </Title>
                      </div>
                      {department.description && (
                        <Paragraph ellipsis={{ rows: 2 }} className="!mb-0">
                          {department.description}
                        </Paragraph>
                      )}
                    </div>

                    {/* Statistiques */}
                    <div className="space-y-3 mb-4">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Statistic
                            title="Employés"
                            value={departmentEmployees.length}
                            prefix={<UserOutlined />}
                            valueStyle={{ fontSize: "16px" }}
                          />
                        </Col>
                        <Col span={12}>
                          <div className="text-center">
                            <Text type="secondary" className="text-sm">
                              Statut
                            </Text>
                            <div className="mt-1">
                              <Tag
                                color={department.is_active ? "green" : "red"}
                              >
                                {department.is_active ? "Actif" : "Inactif"}
                              </Tag>
                            </div>
                          </div>
                        </Col>
                      </Row>

                      {manager && (
                        <div>
                          <Text type="secondary" className="text-sm">
                            Manager :
                          </Text>
                          <div className="mt-1">
                            <Text strong>
                              {manager.first_name} {manager.last_name}
                            </Text>
                          </div>
                        </div>
                      )}

                      {department.budget_annual && (
                        <div>
                          <Text type="secondary" className="text-sm">
                            Budget :
                          </Text>
                          <div className="mt-1">
                            <Text strong className="text-green-600">
                              <DollarOutlined className="mr-1" />
                              {formatCurrency(department.budget_annual)}
                            </Text>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Employés récents */}
                    {departmentEmployees.length > 0 && (
                      <>
                        <Divider className="!my-3" />
                        <div>
                          <Text type="secondary" className="text-sm">
                            Employés récents
                          </Text>
                          <List
                            size="small"
                            className="mt-2"
                            dataSource={departmentEmployees.slice(0, 3)}
                            renderItem={(employee: HREmployee) => (
                              <List.Item className="!py-1 !px-0">
                                <List.Item.Meta
                                  avatar={
                                    <Avatar
                                      size="small"
                                      style={{ backgroundColor: "#1890ff" }}
                                    >
                                      {employee.first_name?.[0]}
                                      {employee.last_name?.[0]}
                                    </Avatar>
                                  }
                                  title={
                                    <Text className="text-sm">
                                      {employee.first_name} {employee.last_name}
                                    </Text>
                                  }
                                  description={
                                    <Text type="secondary" className="text-xs">
                                      {employee.position_title ||
                                        "Poste non défini"}
                                    </Text>
                                  }
                                />
                              </List.Item>
                            )}
                          />
                          {departmentEmployees.length > 3 && (
                            <Text type="secondary" className="text-xs">
                              +{departmentEmployees.length - 3} autres employés
                            </Text>
                          )}
                        </div>
                      </>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Title level={4}>Aucun département</Title>
                  <Text type="secondary">
                    Commencez par créer votre premier département.
                  </Text>
                </div>
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowForm(true)}
              >
                Créer un département
              </Button>
            </Empty>
          </Card>
        )}

        {/* Modal de formulaire */}
        {showForm && (
          <DepartmentForm
            department={editingDepartment}
            onClose={() => {
              setShowForm(false);
              setEditingDepartment(null);
            }}
          />
        )}
      </Content>
    </Layout>
  );
};

export default DepartmentList;
